import React, { useEffect, useState, useCallback, useRef, useMemo, useLayoutEffect } from "react";
import * as global from '../data/global';
import { saveSelectedSite } from '../data/global';
import { MuiMaterials } from '../@index.js';
import BinPicker from "./BinPicker";
import CablePicker from "./CablePicker";
import Scan from "./Scan";
import { useLocation, useNavigate } from "react-router-dom";
import { createApiEndpoint } from "../index.js";
import {
    createWindowChromeTheme,
    getWindowChromeThemeColor,
    ModalBody,
    ModalContentTile,
    SiteSelectPhase,
    SiteWindowHeaderLeading,
    SiteWindowToolNav,
    ChromeHeaderBar,
    Spinner,
    SITE_WINDOW_IDS,
    ALL_SITES_OPTION,
    openSiteWindow,
    outlinedActionButtonSx,
} from "./windowChrome/index.js";
import { isHumiditySensor } from "./siteSetupDefaults.js";

const getThemeColor = getWindowChromeThemeColor;

/** Show -E- when last good temperature read is older than this (UI only; does not change server data). */
const SCAN_SENSOR_STALE_MS = 24 * 60 * 60 * 1000;

function findSensorLastGoodTempCheck(sensorId) {
    const sid = Number(sensorId);
    if (!Number.isFinite(sid)) return null;
    const bins = global.gw.selectedSite?.data?.bins;
    if (!bins) return null;
    for (let b = 0; b < bins.length; b++) {
        const cableList = bins[b].cableList;
        if (!cableList) continue;
        for (let c = 0; c < cableList.length; c++) {
            const sensorList = cableList[c].sensorList;
            if (!sensorList) continue;
            for (let s = 0; s < sensorList.length; s++) {
                const sen = sensorList[s];
                if (Number(sen.id) === sid) return sen.lastGoodTempCheck ?? null;
            }
        }
    }
    return null;
}

/**
 * @param {string} rawTemperature trimmed or untrimmed display value from API
 * @param {string|null|undefined} lastGoodTempCheck ISO date from sensor (site data)
 * @returns {string} original value, or -E- if stale / missing last good read
 */
function displayTemperatureForScanWindow(rawTemperature, lastGoodTempCheck) {
    const t = typeof rawTemperature === "string" ? rawTemperature.trim() : String(rawTemperature ?? "");
    if (/^x[0-9A-Fa-f]{2}$/.test(t)) return t;
    if (t === "-E-" || t === "-e-") return "-E-";
    let checkMs = null;
    if (lastGoodTempCheck) {
        const d = new Date(lastGoodTempCheck);
        if (!Number.isNaN(d.getTime())) checkMs = d.getTime();
    }
    if (checkMs == null) return "-E-";
    if (Date.now() - checkMs > SCAN_SENSOR_STALE_MS) return "-E-";
    return t;
}

function patchSiteSensorFromSnapshot(sensorId, temperature, lastGoodTempCheck) {
    const sid = Number(sensorId);
    if (!Number.isFinite(sid)) return;
    const bins = global.gw.selectedSite?.data?.bins;
    if (!bins) return;
    for (let b = 0; b < bins.length; b++) {
        const cableList = bins[b].cableList;
        if (!cableList) continue;
        for (let c = 0; c < cableList.length; c++) {
            const sensorList = cableList[c].sensorList;
            if (!sensorList) continue;
            for (let s = 0; s < sensorList.length; s++) {
                const sen = sensorList[s];
                if (Number(sen.id) === sid) {
                    if (temperature != null && temperature !== "") {
                        sen.temperature = temperature;
                    }
                    if (lastGoodTempCheck) {
                        sen.lastGoodTempCheck = lastGoodTempCheck;
                    }
                    return;
                }
            }
        }
    }
}

function patchSiteCableSensorsFromHistories(cableId, histories) {
    const cid = Number(cableId);
    const bins = global.gw.selectedSite?.data?.bins;
    if (!Number.isFinite(cid) || !bins || !histories?.length) return;
    for (let b = 0; b < bins.length; b++) {
        const cableList = bins[b].cableList;
        if (!cableList) continue;
        for (let c = 0; c < cableList.length; c++) {
            const cable = cableList[c];
            if (Number(cable.id) !== cid) continue;
            const list = (cable.sensorList || []).filter((s) => !isHumiditySensor(s));
            for (let i = 0; i < histories.length && i < list.length; i++) {
                const h = histories[i];
                const rawTemp = (typeof h.temperature === "string" ? h.temperature : String(h.temperature ?? "")).trim();
                if (rawTemp) list[i].temperature = rawTemp;
                if (h.lastGoodTempCheck) list[i].lastGoodTempCheck = h.lastGoodTempCheck;
            }
            return;
        }
    }
}

const updateCables = (cableData) => {
    if (!cableData?.sensorHistories?.length) return;

    const cid = String(cableData.cableId);
    let dateNode =
        window.document.querySelector(`[data-cable-id="${cid}"][class*="dateRead"]`) ||
        window.document.querySelector(`.toprow[data-cable-id="${cid}"] [class*="dateRead"]`) ||
        window.document.querySelector(`[class*="dateRead"][cableId="${cid}"]`) ||
        window.document.querySelector(`[class*="dateRead"][cableid="${cid}"]`);

    const dateReadSource = cableData.dateRead || global.gw.selectedSite.lastScan;
    let selectedMark = null;
    if (dateReadSource) {
        global.gw.selectedSite.lastScan = new Date(String(dateReadSource).replace("Z", "-05:00")).toLocaleString("en-us", global.dateFormat).replaceAll("/", "-").replace(",", "");
        selectedMark = global.gw.selectedSite.marks.filter(x => x.label === global.gw.selectedSite.lastScan)[0];
        if (!selectedMark) {
            selectedMark = {
                label: global.gw.selectedSite.lastScan,
                value: global.gw.selectedSite.marks.length,
                sensorHistories: []
            };
        }
    }

    if (dateNode && global.gw.selectedSite.lastScan) {
        dateNode.innerHTML = global.gw.selectedSite.lastScan;

        let dateLabel = window.document.getElementsByClassName("dateLabel")[0];
        if (dateLabel) {
            dateLabel.children[0].innerHTML = global.gw.selectedSite.lastScan;
        }
    }

    patchSiteCableSensorsFromHistories(cableData.cableId, cableData.sensorHistories);

    for (let i = 0; i < cableData.sensorHistories.length; i++) {
        let sensorData = cableData.sensorHistories[i];
        if (!sensorData) continue;

        const rawTemp = (typeof sensorData.temperature === "string"
            ? sensorData.temperature
            : String(sensorData.temperature ?? "")).trim();
        const lastGood = sensorData.lastGoodTempCheck ||
            findSensorLastGoodTempCheck(sensorData.sensorId) ||
            (rawTemp && rawTemp !== "-E-" ? new Date().toISOString() : null);
        patchSiteSensorFromSnapshot(
            sensorData.sensorId,
            rawTemp,
            lastGood
        );
        const displayTemp = displayTemperatureForScanWindow(rawTemp, lastGood);

        const sid = String(sensorData.sensorId);
        const row = window.document.querySelector(`.toprow[data-cable-id="${cid}"]`);
        const orderedCell = row ? row.querySelectorAll(".scanSensor")[i] : null;
        let sensorNode =
            orderedCell ||
            window.document.querySelector(`[data-sensor-id="${sid}"]`) ||
            window.document.querySelector(`[sensorId="${sid}"]`) ||
            window.document.querySelector(`[sensorid="${sid}"]`);
        if (sensorNode) {
            sensorNode.style.color = global.getTemperatureColorString(displayTemp);
            sensorNode.innerHTML = displayTemp;
            global.updateSensorTemperature(null, null, sensorData.sensorId, displayTemp, sensorData.lastTemperature);
        }

        if (selectedMark) {
            for (let s = 0; s < global.gw.selectedSite.layoutSensors.length; s++) {
                let layoutSensor = global.gw.selectedSite.layoutSensors[s];
                if (layoutSensor.sensorId === sensorData.sensorId) {
                    layoutSensor.sensorValueString = displayTemp;
                    layoutSensor.sensorValueNumber = parseFloat(displayTemp);

                    selectedMark.sensorHistories.push({
                        sensorId: layoutSensor.sensorId,
                        sensorName: layoutSensor.sensorName,
                        temperature: displayTemp,
                    });
                    break;
                }
            }
        }
    }

    if (selectedMark && dateNode && !global.gw.selectedSite.marks.any(x => x.label === selectedMark.label)) {
        global.gw.selectedSite.marks.splice(global.gw.selectedSite.marks.length - 1, 1);
        global.gw.selectedSite.marks.push(selectedMark);
        global.gw.selectedSite.selectedMark = selectedMark;

        let markLabels = window.document.getElementsByClassName("MuiSlider-markLabel");
        if (markLabels) {
            Array.from(markLabels).map((markLabel, i) => {
                if (global.gw.selectedSite.selectedMark.value === i + 1) {
                    markLabel.style.display = "flex";
                    markLabel.classList.add("mark-selected");
                    markLabel.innerHTML = global.gw.selectedSite.lastScan;
                }
                else {
                    markLabel.style.display = "none";
                    markLabel.classList.remove("mark-selected");
                }
            });
        }
    }
};

const readBinsAndCablesFromSite = () => {
    const hasDataBins = global.gw.selectedSite.data?.bins && global.gw.selectedSite.data.bins.length > 0;
    const hasCables = global.gw.selectedSite.cables && global.gw.selectedSite.cables.length > 0;
    const hasBins = global.gw.selectedSite.bins && global.gw.selectedSite.bins.length > 0;
    let binIds = [];
    if (hasDataBins) {
        binIds = global.gw.selectedSite.data.bins.map((b) => b.binId);
    } else if (hasBins) {
        binIds = global.gw.selectedSite.bins.map((b) => b.binId);
    }
    let cableIds = hasCables ? global.gw.selectedSite.cables.map((c) => c.id) : [];
    if (cableIds.length === 0 && hasDataBins) {
        for (const bin of global.gw.selectedSite.data.bins) {
            for (const cable of bin.cableList || []) {
                if (cable?.id != null && cable.id !== "") cableIds.push(cable.id);
            }
        }
    }
    return {
        binIds,
        cableIds,
        ready: (hasDataBins || hasBins) && cableIds.length > 0,
    };
};

const ScanWindow = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const autoLoadHandledRef = useRef(false);

    const [forceUpdate, setForceUpdate] = useState(0);
    const [phase, setPhase] = useState("select");
    const [sites, setSites] = useState([]);
    const [sitesLoading, setSitesLoading] = useState(true);
    const carriedSiteGuid =
        (location.state?.autoLoad && location.state?.siteGuid) || location.state?.siteGuid || "";
    const [pickerSiteGuid, setPickerSiteGuid] = useState(() => carriedSiteGuid || "");
    const [loadedSiteGuid, setLoadedSiteGuid] = useState("");
    const [loading, setLoading] = useState(() => Boolean(carriedSiteGuid && location.state?.autoLoad));

    const [selectedCables, setSelectedCables] = useState([]);
    const [selectedCablesChanged, setSelectedCablesChanged] = useState(false);
    const [selectedBins, setSelectedBins] = useState([]);
    const [allBinsChecked, setAllBinsChecked] = useState(true);
    const [allCablesChecked, setAllCablesChecked] = useState(true);
    const blinkingCableIdRef = useRef(null);
    const selectedCablesRef = useRef(selectedCables);
    selectedCablesRef.current = selectedCables;

    const { theme, textPrimary, textSecondary, paper, border, pageBg } = useMemo(
        () => createWindowChromeTheme(),
        [forceUpdate],
    );

    useEffect(() => {
        const handleThemeChange = () => {
            setForceUpdate((prev) => prev + 1);
        };
        window.addEventListener("themeChanged", handleThemeChange);
        return () => window.removeEventListener("themeChanged", handleThemeChange);
    }, []);

    useEffect(() => {
        const applySiteInfos = () => {
            const all = [...(global.gw.siteInfos || [])].filter((s) => s.siteGuid && !s.isHms);
            all.sort((a, b) =>
                (a.siteName || "").toLowerCase().localeCompare((b.siteName || "").toLowerCase()),
            );
            setSites(all);
            setSitesLoading(false);
        };

        const hydrateSiteInfosFromResponse = (response) => {
            const fetched = response?.data?.fetchUserAttribute?.sites;
            if (!Array.isArray(fetched) || fetched.length === 0) return;
            if ((global.gw?.siteInfos?.length ?? 0) > 0) return;
            global.gw.siteInfos = fetched
                .map((site, siteIndex) => ({
                    siteIndex,
                    siteGuid: site.siteGuid,
                    siteName: site.main?.siteName ?? "Site",
                    lastScan: site.lastScan,
                    isHms: site.isHms,
                    bins: site.bins ?? [],
                }))
                .filter((s) => s.siteGuid && !s.isHms);
        };

        let cancelled = false;
        const loadSites = async () => {
            if ((global.gw.siteInfos || []).length > 0) {
                if (!cancelled) applySiteInfos();
                return;
            }
            if (!global.gw?.token && !global.gw?.bolts) {
                if (!cancelled) setSitesLoading(false);
                return;
            }
            try {
                const response = await createApiEndpoint().fetchUserSites("direct");
                if (cancelled) return;
                hydrateSiteInfosFromResponse(response);
            } catch (err) {
                if (!cancelled) console.warn("ScanWindow: fetchUserSites", err);
            } finally {
                if (!cancelled) applySiteInfos();
            }
        };

        loadSites();
        return () => {
            cancelled = true;
        };
    }, [forceUpdate]);

    useLayoutEffect(() => {
        if (!location.state?.autoLoad || !location.state?.siteGuid) return;
        setPickerSiteGuid(location.state.siteGuid);
    }, [location.state?.autoLoad, location.state?.siteGuid]);

    const applySiteSelection = useCallback(async (siteGuid) => {
        if (!siteGuid) return;
        if (siteGuid === ALL_SITES_OPTION) {
            openSiteWindow(SITE_WINDOW_IDS.sitesScanning, ALL_SITES_OPTION);
            return;
        }
        setLoading(true);
        try {
            const want = global.normalizeGuidForCompare
                ? global.normalizeGuidForCompare(siteGuid)
                : String(siteGuid || "").toLowerCase();
            const info = (global.gw.siteInfos || []).find((s) => {
                const g = s?.siteGuid;
                if (!g) return false;
                const have = global.normalizeGuidForCompare
                    ? global.normalizeGuidForCompare(g)
                    : String(g).toLowerCase();
                return have === want;
            });

            if (info) {
                if (global.gw.selectedSite?.refreshId) {
                    clearTimeout(global.gw.selectedSite.refreshId);
                }
                if (
                    !global.gw.selectedSite
                    || global.normalizeGuidForCompare?.(global.gw.selectedSite.siteGuid)
                        !== global.normalizeGuidForCompare?.(info.siteGuid)
                ) {
                    global.gw.selectedSite = new global.Site();
                }
                global.gw.selectedSite.siteIndex = info.siteIndex;
                global.gw.selectedSite.siteGuid = info.siteGuid;
                global.gw.selectedSite.siteName = info.siteName;
                global.gw.selectedSite.lastScan = info.lastScan;
                // Drop prior site's scan poll / snapshot state (in-flight fetches ignore via siteGuid).
                global.gw.selectedSite.activeScanCableIds = null;
                global.gw.selectedSite.appliedScanSensorCables = [];
                global.gw.selectedSite.pendingScanSensorFetches = {};
                global.gw.selectedSite.finishedCables = [];
                global.gw.selectedSite.addScanInFlight = false;
                global.gw.selectedSite.directScanStatusRows = null;
                global.gw.selectedSite.directScanId = null;
                saveSelectedSite?.();
            } else if (global.gw.selectedSite) {
                global.gw.selectedSite.siteGuid = siteGuid;
            }

            await global.initializeCustomerSite(navigate);
            const loadedName =
                global.gw.selectedSite?.siteName
                || global.gw.selectedSite?.main?.siteName
                || global.gw.selectedSite?.data?.main?.siteName;
            if (loadedName) {
                global.gw.selectedSite.siteName = loadedName;
            }

            let retryCount = 0;
            const maxRetries = 30;
            while (retryCount < maxRetries) {
                const { binIds, cableIds, ready } = readBinsAndCablesFromSite();
                if (ready) {
                    setSelectedBins(binIds);
                    setSelectedCables(cableIds);
                    setAllBinsChecked(true);
                    setAllCablesChecked(true);
                    break;
                }
                await new Promise((resolve) => setTimeout(resolve, 100));
                retryCount++;
            }
            if (retryCount >= maxRetries) {
                const { binIds, cableIds } = readBinsAndCablesFromSite();
                setSelectedBins(binIds);
                setSelectedCables(cableIds);
            }

            setLoadedSiteGuid(info?.siteGuid || siteGuid);
            setPickerSiteGuid(info?.siteGuid || siteGuid);
            setPhase("view");
        } catch (e) {
            console.error("ScanWindow: failed to load site", e);
            setPhase("select");
            setLoadedSiteGuid("");
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    useEffect(() => {
        const siteGuid = location.state?.siteGuid;
        if (!siteGuid || !location.state?.autoLoad) return;
        if (autoLoadHandledRef.current === siteGuid) return;
        autoLoadHandledRef.current = siteGuid;
        setPickerSiteGuid(siteGuid);
        applySiteSelection(siteGuid);
    }, [location.state, applySiteSelection]);

    const handleContinue = useCallback((guid) => {
        const next = guid || pickerSiteGuid;
        if (!next) return;
        setPickerSiteGuid(next);
        applySiteSelection(next);
    }, [pickerSiteGuid, applySiteSelection]);

    const handleHeaderSiteChange = useCallback((siteGuid) => {
        if (!siteGuid) return;
        setPickerSiteGuid(siteGuid);
        applySiteSelection(siteGuid);
    }, [applySiteSelection]);

    const handleClose = () => {
        navigate("/sites", { replace: true });
    };

    const readRowCableId = (row) => {
        const a = row.getAttribute("data-cable-id");
        if (a != null && a !== "") return Number(a);
        const b = row.getAttribute("cableId");
        if (b != null && b !== "") return Number(b);
        return NaN;
    };

    const applyBlinkClass = (id) => {
        const cableRows = window.document.getElementsByClassName("toprow");
        for (let c = 0; c < cableRows.length; c++) {
            cableRows[c].classList.remove("scan-cable-blinking");
            cableRows[c].style.visibility = "";
        }
        if (id == null || id === "" || id === false) return;
        const targetId = Number(id);
        if (!Number.isFinite(targetId)) return;

        const scanScope = global.gw.selectedSite.activeScanCableIds;
        const visibleCables = (Array.isArray(scanScope) && scanScope.length > 0)
            ? scanScope
            : (selectedCablesRef.current || []);
        if (visibleCables.length > 0 && !visibleCables.some((cid) => Number(cid) === targetId)) {
            return;
        }

        for (let c = 0; c < cableRows.length; c++) {
            const cableRow = cableRows[c];
            if (readRowCableId(cableRow) !== targetId) continue;
            if (cableRow.style.display === "none") {
                cableRow.style.display = "flex";
            }
            let p = cableRow.parentElement;
            for (let g = 0; p && g < 40 && !(p.classList && p.classList.contains("scanWindow")); g++) {
                if (p.style && p.style.display === "none") {
                    p.style.display = "";
                }
                p = p.parentElement;
            }
            cableRow.classList.add("scan-cable-blinking");
            break;
        }
    };

    useLayoutEffect(() => {
        applyBlinkClass(blinkingCableIdRef.current);
    });

    const activeSiteGuid = loadedSiteGuid || pickerSiteGuid || "";

    const renderScanBinData = (nodes) => {
        if (nodes.className === "toprow") {
            const cid = nodes.cableId != null && nodes.cableId !== "" ? String(nodes.cableId) : "";
            const bid = nodes.binId != null && nodes.binId !== "" ? String(nodes.binId) : "";
            return (
                <div
                    key={nodes.key}
                    id={nodes.binId}
                    className={
                        cid && Number(cid) === Number(blinkingCableIdRef.current)
                            ? `${nodes.className} scan-cable-blinking`
                            : nodes.className
                    }
                    style={nodes.style}
                    data-cable-id={cid || undefined}
                    data-bin-id={bid || undefined}
                >
                    {Array.isArray(nodes.children) ? nodes.children.map((node) => renderScanBinData(node)) : nodes.value}
                </div>
            );
        }
        return (
            <MuiMaterials.Box
                key={nodes.key}
                id={nodes.binId}
                binId={nodes.binId}
                cableId={nodes.cableId}
                data-cable-id={nodes.cableId != null && nodes.cableId !== '' ? String(nodes.cableId) : undefined}
                sensorId={nodes.sensorId}
                data-sensor-id={nodes.sensorId != null && nodes.sensorId !== '' ? String(nodes.sensorId) : undefined}
                name={nodes.name}
                className={nodes.className}
                sx={nodes.style}
            >
                {Array.isArray(nodes.children) ? nodes.children.map((node) => renderScanBinData(node)) : nodes.value}
            </MuiMaterials.Box>
        );
    };

    const getScanData = () => {
        let node = {
            style: {
                display: "block",
                userSelect: "text",
                margin: "0 0 0 10px",
                textWrap: "nowrap",
                alignSelf: "flex-start",
            },
            children: []
        };

        if (global.gw.selectedSite.data && global.gw.selectedSite.data.bins) {
            node.key = global.gw.selectedSite.data.siteGuid;
            node.id = global.gw.selectedSite.data.siteGuid;
            node.name = global.gw.selectedSite.data.siteGuid;
            node.value = global.gw.selectedSite.data.siteName;
            for (let b = 0; b < global.gw.selectedSite.data.bins.length; b++) {
                let binData = global.gw.selectedSite.data.bins[b];
                for (let c = 0; c < binData.cableList.length; c++) {
                    let cableData = binData.cableList[c];
                    let rowNode = {
                        style: {
                            height: "12px",
                            textAlign: "left",
                            textWrap: "nowrap",
                            display: "flex",
                            alignItems: "center",
                        },
                        children: []
                    };
                    //let dateRead = `${cableData.dateRead.substring(5, 10)}-${cableData.dateRead.substring(2, 4)} ${cableData.dateRead.substring(11, 16)}`;
                    let dateRead = new Date(cableData.dateRead.replace("Z", "-05:00")).toLocaleString("en-us", global.dateFormat).replaceAll("/", "-").replace(",", "");

                    rowNode.children.push({
                        key: `${c}_${binData.binId}_${cableData.id}_dateRead`,
                        id: `${c}_${binData.binId}_${cableData.id}_dateRead`,
                        name: `${c}_${binData.binId}_${cableData.id}_dateRead`,
                        value: dateRead,
                        cableId: cableData.id,
                        className: "scanrow dateRead",
                        style: {
                            userSelect: "text",
                            color: getThemeColor('#333333', '#f4cc8e'),
                            flex: "0 0 auto",
                            width: "auto",
                            minWidth: "11.5em",
                            paddingRight: "8px",
                            fontSize: "8px",
                        },
                    });
                    rowNode.key = `${binData.binId}_${cableData.id}`;
                    rowNode.binId = `${binData.binId}`;
                    rowNode.cableId = `${cableData.id}`;
                    rowNode.id = rowNode.key;
                    rowNode.name = rowNode.id;
                    rowNode.className = "toprow";
                    rowNode.children.push({
                        key: `${c}_${binData.binId}_${cableData.id}`,
                        id: `${c}_${binData.binId}_${cableData.id}`,
                        name: `${c}_${binData.binId}_${cableData.id}`,
                        value: cableData.name,
                        className: "scanrow scanCable",
                        style: {
                            userSelect: "text",
                            color: getThemeColor('#333333', '#f4cc8e'),
                            flex: "0 0 auto",
                            width: "auto",
                            minWidth: "8em",
                            paddingRight: "8px",
                            fontSize: "8px",
                            overflow: "visible",
                        },
                    });
                    for (let s = 0; s < cableData.sensorList.length; s++) {
                        let sensorData = cableData.sensorList[s];
                        const displayTemp = displayTemperatureForScanWindow(
                            sensorData.temperature,
                            sensorData.lastGoodTempCheck
                        );
                        let color = global.getTemperatureColorString(displayTemp);
                        rowNode.children.push({
                            key: `${c}_${binData.binId}_${cableData.id}_${s}_${sensorData.id}`,
                            id: `${c}_${binData.binId}_${cableData.id}_${s}_${sensorData.id}`,
                            name: `${c}_${binData.binId}_${cableData.id}_${s}_${sensorData.id}`,
                            value: displayTemp,
                            sensorId: sensorData.id,
                            className: "scanrow scanSensor",
                            style: {
                                userSelect: "text",
                                color: color,
                                flex: "0 0 20px",
                                width: "20px",
                                minWidth: "20px",
                                maxWidth: "20px",
                                fontSize: "8px",
                                justifyContent: "center",
                            },
                        });
                    }
                    node.children.push(rowNode);
                }
            }
        }
        return node;
    }

    const binSelected = (bid, isShow) => {
        let binRows = window.document.querySelectorAll(`[data-bin-id='${bid}'],[binId='${bid}']`);
        
        // Update selected bins - use functional update to ensure we have the latest state
        setSelectedBins(prevBins => {
            // Ensure we have a valid array (fallback to empty array if prevBins is undefined/null)
            let tempBins = prevBins && prevBins.length > 0 ? [...prevBins] : [];
            if (isShow) {
                if (!tempBins.includes(bid)) {
                    tempBins.push(bid);
                }
            } else {
                tempBins = tempBins.filter(b => b !== bid);
            }
            return tempBins;
        });
        
        if (binRows) {
            // Update selected cables - use functional update to ensure we have the latest state
            setSelectedCables(prevCables => {
                // Ensure we have a valid array (fallback to empty array if prevCables is undefined/null)
                let tempCables = prevCables && prevCables.length > 0 ? [...prevCables] : [];
                
                for (var b = 0; b < binRows.length; b++) {
                    if (isShow) {
                        binRows[b].style.display = "flex";
                        const cableId = Number(binRows[b].getAttribute("data-cable-id") || binRows[b].getAttribute("cableId"));
                        if (!tempCables.includes(cableId)) {
                            tempCables.push(cableId);
                        }
                    }
                    else {
                        binRows[b].style.display = "none";
                        const cableId = binRows[b].getAttribute("data-cable-id") || binRows[b].getAttribute("cableId");
                        tempCables = tempCables.filter(c => c.toString() !== cableId);
                    }
                }
                return tempCables;
            });
        }
        setSelectedCablesChanged(!selectedCablesChanged);
    }

    const cableSelected = (id, isShow) => {
        let cableRows = window.document.querySelectorAll(
            `.toprow[data-cable-id='${id}'],[class*='toprow'][cableId='${id}']`
        );
        if (cableRows) {
            // Update selected cables - use functional update to ensure we have the latest state
            setSelectedCables(prevCables => {
                // Ensure we have a valid array (fallback to empty array if prevCables is undefined/null)
                let tempCables = prevCables && prevCables.length > 0 ? [...prevCables] : [];
                
                for (let c = 0; c < cableRows.length; c++) {
                    if (isShow) {
                        cableRows[c].style.display = "flex";
                        const cableId = Number(cableRows[c].getAttribute("data-cable-id") || cableRows[c].getAttribute("cableId"));
                        if (!tempCables.includes(cableId)) {
                            tempCables.push(cableId);
                        }
                    }
                    else {
                        cableRows[c].style.display = "none";
                        const cableId = cableRows[c].getAttribute("data-cable-id") || cableRows[c].getAttribute("cableId");
                        tempCables = tempCables.filter(c => c.toString() !== cableId);
                    }
                }
                return tempCables;
            });
        }
        setSelectedCablesChanged(!selectedCablesChanged);
    }

    const selectedAll = (isShow) => {
        let binRows = window.document.getElementsByClassName("toprow");
        if (binRows) {
            if (isShow) {
                // Select all bins
                if (global.gw.selectedSite.data?.bins) {
                    setSelectedBins(global.gw.selectedSite.data.bins.map(b => { return b.binId; }));
                }
            } else {
                // Deselect all bins
                setSelectedBins([]);
            }
            
            // Update selected cables - use functional update to ensure we have the latest state
            setSelectedCables(prevCables => {
                let tempCables = [];
                for (var b = 0; b < binRows.length; b++) {
                    if (isShow) {
                        binRows[b].style.display = "flex";
                        const cableId = Number(binRows[b].getAttribute("data-cable-id") || binRows[b].getAttribute("cableId"));
                        if (!tempCables.includes(cableId)) {
                            tempCables.push(cableId);
                        }
                    }
                    else {
                        binRows[b].style.display = "none";
                    }
                }
                return tempCables;
            });
        }
        setSelectedCablesChanged(!selectedCablesChanged);
    }

    const cableScanning = (id) => {
        if (id == null || id === "" || id === false) {
            blinkingCableIdRef.current = null;
            applyBlinkClass(null);
            return;
        }
        const targetId = Number(id);
        blinkingCableIdRef.current = Number.isFinite(targetId) ? targetId : null;
        applyBlinkClass(blinkingCableIdRef.current);
    };

    return (
        <MuiMaterials.ThemeProvider theme={theme}>
            <div
                className="scanWindow"
                style={{
                    position: "fixed",
                    inset: 0,
                    zIndex: 1000,
                    display: "flex",
                    flexDirection: "column",
                    background: pageBg,
                    color: textPrimary,
                }}
            >
                {loading ? (
                    <MuiMaterials.Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            zIndex: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: getThemeColor("rgba(232,232,232,0.7)", "rgba(26,26,26,0.7)"),
                        }}
                    >
                        <Spinner size="modal" />
                    </MuiMaterials.Box>
                ) : null}

                <ChromeHeaderBar
                    paper={paper}
                    border={border}
                    textSecondary={textSecondary}
                    onClose={handleClose}
                    leading={
                        phase === "view" ? (
                            <SiteWindowHeaderLeading
                                toolTitle="Scan"
                                siteWindowId={SITE_WINDOW_IDS.scan}
                                siteGuid={activeSiteGuid || null}
                                textSecondary={textSecondary}
                                border={border}
                                showSiteSelect
                                siteSelectValue={activeSiteGuid}
                                onSiteChange={handleHeaderSiteChange}
                                sites={sites}
                                filterSite={(s) => !s.isHms}
                                siteSelectDisabled={loading || sitesLoading}
                                includeAllSites={Boolean(global.isAdminUser?.(global.gw.email))}
                                includeTool={false}
                            />
                        ) : null
                    }
                    tool={
                        <SiteWindowToolNav
                            toolTitle="Scan"
                            siteWindowId={SITE_WINDOW_IDS.scan}
                            siteGuid={activeSiteGuid || null}
                            textSecondary={textSecondary}
                        />
                    }
                    actions={
                        phase === "view" && !loading ? (
                            <>
                                {global.isAdminUser?.(global.gw.email) ? (
                                    <MuiMaterials.Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => openSiteWindow(SITE_WINDOW_IDS.sitesScanning)}
                                        sx={{ whiteSpace: "nowrap", flexShrink: 0, ...outlinedActionButtonSx() }}
                                    >
                                        Sites Scanning
                                    </MuiMaterials.Button>
                                ) : null}
                                <BinPicker
                                    key={`scan-bins-${activeSiteGuid}`}
                                    windowStyle={{
                                        zIndex: 100,
                                    }}
                                    selected={binSelected}
                                    selectedAll={selectedAll}
                                    selectedCables={selectedCables}
                                    selectedBins={selectedBins}
                                    allCheckboxChecked={allBinsChecked}
                                    onAllCheckboxChange={setAllBinsChecked}
                                    syncAllCheckbox={allCablesChecked}
                                />
                                <CablePicker
                                    key={`scan-cables-${activeSiteGuid}`}
                                    windowStyle={{
                                        zIndex: 100,
                                    }}
                                    selected={cableSelected}
                                    selectedAll={selectedAll}
                                    selectedBins={selectedBins}
                                    allCheckboxChecked={allCablesChecked}
                                    onAllCheckboxChange={setAllCablesChecked}
                                    syncAllCheckbox={allBinsChecked}
                                />
                                <Scan
                                    key={`scan-actions-${activeSiteGuid}`}
                                    windowStyle={{
                                        zIndex: "100",
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                    cableScanning={cableScanning}
                                    selectedCables={selectedCables}
                                    updateCables={updateCables}
                                />
                            </>
                        ) : null
                    }
                />

                {phase === "select" ? (
                    <SiteSelectPhase
                        value={pickerSiteGuid}
                        onChange={setPickerSiteGuid}
                        onContinue={handleContinue}
                        onCancel={handleClose}
                        loading={sitesLoading || loading}
                        filterSite={(s) => !s.isHms}
                        includeAllSites={Boolean(global.isAdminUser?.(global.gw.email))}
                    />
                ) : null}

                {phase === "view" && !loading ? (
                    <ModalBody className="hide-scrollbar">
                        <ModalContentTile
                            className="hide-scrollbar scanTable"
                            sx={{
                                overflowY: "auto",
                                overflowX: "auto",
                                p: "12px 16px",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                justifyContent: "flex-start",
                            }}
                        >
                            {renderScanBinData(getScanData())}
                        </ModalContentTile>
                    </ModalBody>
                ) : null}
            </div>
        </MuiMaterials.ThemeProvider>
    );
}
export default ScanWindow;